import Stripe from 'stripe';
import { ORG_IDS, PageHref, Routes } from '@/constants';
import { PUBLIC_PAGES } from '@/constants/pages';
import { invokeParentRoute, isSubRoute } from '@/lib/navigationRoutes';
import { CurrentUser, OrganizationUserRole, UserRole } from '@/server/services/Auth0/Auth0.types';
import { rolloutService } from '../services/RolloutService';
import { OrganizationModel } from './OrganizationModel';

// README: Needed for avoid Dynamic Code Evaluation (e. g. 'eval', 'new Function', 'WebAssembly.compile') not allowed in Edge Runtime
const isEmpty = (arr: unknown[] | undefined) => !arr || arr.length === 0;

export class CurrentUserModel {
  private user: Partial<CurrentUser>;
  id: CurrentUser['id'];
  name: CurrentUser['name'];
  first_name: CurrentUser['first_name'];
  last_name: CurrentUser['last_name'];
  email: CurrentUser['email'];
  email_verified: CurrentUser['email_verified'];
  phone_number: CurrentUser['phone_number'];
  user_id: CurrentUser['user_id'];
  org_id: CurrentUser['org_id'];
  logins_count: CurrentUser['logins_count'];
  session_id: CurrentUser['session_id'];
  organization: CurrentUser['organization'];
  created_at: CurrentUser['created_at'];
  organisation_name: CurrentUser['organisation_name'];
  newsletter_consent: boolean;
  private organizationModel: OrganizationModel | null;

  constructor(user: Partial<CurrentUser> = {}) {
    this.id = user.id;
    this.user = user;
    this.user_id = user.user_id!;
    this.logins_count = user.logins_count!;
    this.first_name = user.first_name;
    this.last_name = user.last_name;
    this.name = user?.nickname || user?.name || '';
    this.email = user?.email || '';
    this.email_verified = user?.email_verified || false;
    this.phone_number = user?.phone_number || '';
    this.org_id = user.org_id!;
    this.session_id = user.sid as string;
    this.organization = user.organization || null;
    this.organizationModel = user.organization ? new OrganizationModel(user.organization) : null;
    this.created_at = user.created_at;
    this.organisation_name = user.organisation_name || '';
    this.newsletter_consent = user.newsletterConsent ?? false;
  }

  getRoles = (): UserRole[] => this.user.organizationRoles || [];

  getStripeCustomerId = () => this.user.stripeCustomerId || null;
  getSubscriptions = () => this.user.subscriptions || [];

  private hasRole = (role: OrganizationUserRole) => !!this.user.organizationRoles?.find((item) => item.role === role);

  private matchRole = (value: OrganizationUserRole) => !!this.getRoles().find((item) => item.role?.startsWith(value));

  hasAdminRole = () => this.hasRole('Admin');

  hasActionMenuRole = () =>
    this.hasRole('ActionMenu') || !!this.getRoles().find((item) => item.role?.startsWith('ActionMenu:'));

  hasActionMenuEditorRole = () => this.hasRole('ActionMenuEditor') || this.matchRole('ActionMenuEditor');

  hasDashboardOverviewRole = () => this.hasRole('DashboardOverview');

  isPageAvailable = ({ page }: { page: PageHref }): boolean => {
    const pageAvailable = this.isPageAvailableByPagesList(page);
    const hasPagePaymentAccess = this.hasPaymentAccess() ? this.isActivePaymentSubscription() : true;
    switch (page) {
      case Routes.AUTH_ERROR:
        return false;
      case Routes.REGISTRATION:
        return !this.user_id;
      case Routes.REGISTRATION_PROFILE:
        return !!this.user_id;
      case Routes.REGISTRATION_VERIFY_EMAIL:
        return !!this.user_id && !this.user.email_verified;
      case Routes.REGISTRATION_COMPLETE:
        return !this.org_id;
      case Routes.SETTINGS:
      case Routes.SETTINGS_ACCOUNT:
      case Routes.SETTINGS_CACHE:
        return !!this.user_id;
      case Routes.SETTINGS_SUBSCRIPTION:
        return !!this.user_id && this.hasPaymentAccess();
      case Routes.ORGANIZATIONS:
      case Routes.ORGANIZATIONS_PICK:
        return !this.org_id;
      case Routes.DASHBOARD:
        return hasPagePaymentAccess && pageAvailable;
      case Routes.EMBEDDED_SUPPLY_CHAIN:
        return hasPagePaymentAccess && pageAvailable && rolloutService.isEmbeddedSupplyChainAppEnabled();
      case Routes.EMBEDDED_INTELLIGENCE_DASHBOARD:
        return hasPagePaymentAccess && pageAvailable && rolloutService.isIntelligenceDashboardEmbeddedAppEnabled();
      // TEMP(ankara-poc-demo): Ankara NATO POC demo — remove after the demo.
      case Routes.EMBEDDED_ANKARA_POC:
        return this.org_id === ORG_IDS.NATO;
      case Routes.SUPPLY_CHAIN:
      case Routes.SEARCH_CONSOLE:
      case Routes.NARRATIVE_INTELLIGENCE:
      case Routes.NARRATIVE_INTELLIGENCE_NARRATIVES:
      case Routes.NARRATIVE_INTELLIGENCE_NETWORKS:
      case Routes.GEOPOLITICAL_INTELLIGENCE:
      case Routes.GEOPOLITICAL_INTELLIGENCE_INCIDENTS:
      case Routes.GEOPOLITICAL_INTELLIGENCE_TRADE:
        return hasPagePaymentAccess && pageAvailable;
      case Routes.GEOPOLITICAL_INTELLIGENCE_ACTIONS:
        return hasPagePaymentAccess && pageAvailable && this.hasActionMenuRole();
      case Routes.REPORTS_COPILOT:
        return hasPagePaymentAccess && !!this.organizationModel?.isCopilotAvailable({ type: 'reports' });
      default:
        return PUBLIC_PAGES.includes(page) || (hasPagePaymentAccess && pageAvailable);
    }
  };

  hasPaymentAccess = () => !isEmpty(this.getPaymentProductIds());

  getPaymentProductIds = (): string[] => {
    const raw = this.organization?.metadata?.payment_product_id;
    if (!raw) return [];
    return raw
      .split(',')
      .concat(this.getPaymentDefaultProductId() ?? '')
      .map((s) => s.trim())
      .filter(Boolean);
  };

  getPaymentDefaultProductId = () => this.organization?.metadata?.payment_product_default_id;

  getPaymentProductId = () => {
    return this.organization?.metadata?.payment_product_id;
  };

  getFreeSubscriptionAccess = () => {
    return this.user.hasFreeSubscriptionAccess && this.user.subscriptionFreeAccesses?.length
      ? this.user.subscriptionFreeAccesses[0]
      : undefined;
  };

  hasActivePaymentSubscription = () => {
    return (
      !!this.user.stripeCustomerId &&
      !!this.user.subscriptions?.some(
        (sub) => (sub.status === 'active' || sub.status === 'trialing') && sub.organizationId === this.org_id,
      )
    );
  };

  getSubscriptionStatus = (): Stripe.Subscription.Status | 'none' => {
    const orgSubscription = this.user.subscriptions?.find((sub) => sub.organizationId === this.org_id);

    if (!orgSubscription) return 'none';

    return orgSubscription.status;
  };

  isActivePaymentSubscription = () => {
    return this.user.hasFreeSubscriptionAccess || this.hasActivePaymentSubscription();
  };

  isPageAvailableByPagesList = (page: PageHref) => {
    const pages = this.organizationModel?.pages;
    if (!pages || !pages.length) return false;

    if (isSubRoute(page)) {
      const parentRoute = invokeParentRoute(page);
      return pages.includes(page) || pages.includes(parentRoute);
    } else if (page !== Routes.INDEX) {
      const subRoutes = pages.filter((item) => !!item.startsWith(page));
      return !!subRoutes.length;
    }

    return pages.includes(page);
  };
}
